import {MIN_SPLIT_STOPPAGE_DURATION} from '@hconnect/common/consts'
import {StoppageEvent} from '@hconnect/common/stoppages/types/stoppage.types'
import {DeleteOutlined, EditOutlined, MoreVert} from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import {Box, Divider, IconButton, Menu, MenuItem, Typography} from '@mui/material'
import moment from 'moment-timezone'
import {useCallback, useRef, useState, FC} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'

type StoppageDetailsActionsProps = {
  stoppage: StoppageEvent
  timezone: string
  onEdit?: () => void
  onDelete?: () => void
  onSplit?: () => void
  onClose?: () => void
  isEditStoppageEnabled?: boolean
  isSplitStoppageEnabled?: boolean
  isDeleteStoppageEnabled?: boolean
  isStoppageSubmitSupported: boolean
}

export const StoppageDetailsActions: FC<StoppageDetailsActionsProps> = ({
  stoppage,
  timezone,
  onEdit,
  onDelete,
  onSplit,
  onClose,
  isEditStoppageEnabled = true,
  isSplitStoppageEnabled = true,
  isDeleteStoppageEnabled = true,
  isStoppageSubmitSupported
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const menuButtonRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const openMenu = useCallback(() => setMenuOpen(true), [setMenuOpen])
  const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen])

  const onDeleteClick = () => {
    closeMenu()
    onDelete?.()
  }

  const onSplitClick = () => {
    closeMenu()
    onSplit?.()
  }

  const stoppageDuration = moment
    .tz(stoppage.stoppageEnd || moment.utc(), timezone)
    .diff(moment.tz(stoppage.stoppageStart, timezone), 'minutes')

  const isSubmitted = stoppage.isSubmitted && isStoppageSubmitSupported

  return (
    <Box>
      <IconButton
        sx={{p: 1}}
        ref={menuButtonRef}
        onClick={openMenu}
        color="primary"
        data-test-id="stoppage-details-more-button"
      >
        <MoreVert />
      </IconButton>
      <Menu
        data-test-id="stoppage-details-more-menu"
        anchorEl={menuButtonRef.current}
        open={menuOpen}
        onClose={closeMenu}
      >
        {isEditStoppageEnabled && (
          <MenuItem onClick={onEdit} data-test-id="stoppage-details-edit-button">
            <Typography variant="subtitle1">
              <EditOutlined sx={{mr: 2, verticalAlign: 'text-bottom'}} />
              {t('action.edit')}
            </Typography>
          </MenuItem>
        )}

        {isSplitStoppageEnabled &&
          !isSubmitted &&
          stoppageDuration > MIN_SPLIT_STOPPAGE_DURATION && [
            <Divider key="stoppage-details-split-divider" />,
            <MenuItem
              onClick={onSplitClick}
              key="stoppage-details-split-button"
              data-test-id="stoppage-details-split-button"
            >
              <Typography variant="subtitle1">
                <ContentCutIcon sx={{mr: 2, verticalAlign: 'text-bottom'}} />
                {t('action.split')}
              </Typography>
            </MenuItem>
          ]}

        {isDeleteStoppageEnabled &&
          !isSubmitted && [
            <Divider key="stoppage-details-delete-divider" />,
            <MenuItem
              onClick={onDeleteClick}
              key="stoppage-details-delete-button"
              data-test-id="stoppage-details-delete-button"
            >
              <Typography variant="subtitle1">
                <DeleteOutlined sx={{mr: 2, verticalAlign: 'text-bottom'}} />
                {t('action.delete')}
              </Typography>
            </MenuItem>
          ]}
      </Menu>
      <IconButton
        sx={{p: 1}}
        onClick={onClose}
        color="primary"
        data-test-id="stoppage-details-close"
      >
        <CloseIcon />
      </IconButton>
    </Box>
  )
}
